import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import {
    createTheme as createMuiTheme,
    ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ToastContainer } from "react-toastify";
import { Container as PromisedContainer } from "react-modal-promise";
import "react-toastify/dist/ReactToastify.css";
import { theme, storageVersion } from "@utilities";
import {
    store,
    persistor,
    useAppDispatch,
    useIsLoggedIn,
    useShouldVerifyEmail,
    useIsProfileSetupInProgress,
    useNeedsRealEmail,
} from "@redux";
import { auth_route, public_route, protected_route } from "@routes";
import ProtectedRoute from "./Routes/ProtectedRoute";
import { IRoute } from "@interfaces";
import AuthRoute, { SetupProfileRoute } from "./Routes/AuthRoute";
import PublicRoute from "./Routes/PublicRoute";
import { SetupLoaderComponent, CookieConsentBanner } from "@components";
import { ForgotPassword } from "@pages";
import { BeaconStyles } from "@components";
import { DiscountInfoModalPromise } from "src/Module/Modals";
import { lsHasSeenDiscountModal } from "src/Utilities/storage";
import { useAppSelector } from "@redux";
import { useEffect } from "react";
import { fetchActiveDiscount } from "@module";
import { fetchAllLocations } from "@redux";
import { GlobalStyles } from "./styles/GlobalStyles";
import { useUserResourcesCache } from "@utilities";
import { Slide } from "react-toastify";
import { createGlobalStyle } from "styled-components";
import { AppleOAuthHandler } from "@components";
import { GoogleOAuthHandler } from "./Components/GoogleOAuthHandler";
import { GTMPageView } from "./Components/GTMPageView";
import { SiteJsonLd } from "@components";
import { SignupPasswordModal } from "src/Module/Checkout/Components/SignupPasswordModal";
import { usePointsAgentUpgrade } from "src/Module/Checkout/hooks/usePointsAgentUpgrade";

// Slower toast animation with 0.8s duration for smoother appearance
const ToastStyles = createGlobalStyle`
    .Toastify__slide-enter--bottom-right {
        animation-duration: 0.8s;
    }
    .Toastify__slide-exit--bottom-right {
        animation-duration: 0.8s;
    }
`;

/**
 * MUI's own theme, which the app previously never configured — so every MUI
 * control (text-field labels and underlines, checkboxes, sliders) fell back to
 * MUI's stock blue regardless of which brand was being rendered.
 *
 * Pointing `primary` at the flavour's colours fixes that everywhere at once,
 * rather than overriding `.Mui-*` classes form by form; there are a dozen forms
 * using these inputs.
 */
const muiTheme = createMuiTheme({
    palette: {
        primary: {
            main: theme.colors.main,
            contrastText: theme.colors.button_text,
        },
    },
});

function App() {
    return (
        <Provider store={store}>
            <ThemeProvider theme={theme}>
                <MuiThemeProvider theme={muiTheme}>
                    <GlobalStyles />
                    <ToastStyles />
                    <PersistGate loading={null} persistor={persistor}>
                        <BeaconStyles />
                        <CookieConsentBanner />
                        <div className="App ">
                            <BrowserRouter>
                                <Main />
                            </BrowserRouter>
                        </div>
                    </PersistGate>
                </MuiThemeProvider>
            </ThemeProvider>
        </Provider>
    );
}

function Main() {
    // Redux state
    const dispatch = useAppDispatch();
    const isLoggedIn = useIsLoggedIn();
    const needsRealEmail = useNeedsRealEmail();
    const shouldVerifyEmail = useShouldVerifyEmail();
    const isProfileSetupInProgress = useIsProfileSetupInProgress();
    const activeDiscount = useAppSelector((state) => state.checkout.activeDiscount);

    // Periodically cache user resources every 5 minutes to keep data fresh (only when logged in)
    useUserResourcesCache(isLoggedIn ? 5 : 0);

    // Finishes the points-agent upgrade for an account signed up on a screen that
    // could not complete it itself, once its email comes back verified.
    usePointsAgentUpgrade();

    // App version check
    const appVersion = storageVersion.getItem();
    const currentVersion = "2320061530";
    if (!appVersion || appVersion !== currentVersion) {
        localStorage.clear();
        localStorage.removeItem("persist:root");
        dispatch({ type: "RESET" });
        storageVersion.setItem(currentVersion);
    }
    useEffect(() => {
        dispatch(fetchActiveDiscount());
        // Fetch the full country -> state -> region -> locality tree
        // once on app bootstrap. Cached in Redux and consumed by the
        // header LocationPicker, replacing four cascading TDMS calls.
        dispatch(fetchAllLocations());
    }, []);
    // Show discount modal once per browser
    useEffect(() => {
        if (activeDiscount && activeDiscount.discount > 0 && !lsHasSeenDiscountModal.getItem()) {
            const timeout = setTimeout(() => {
                DiscountInfoModalPromise({
                    isOpen: true,
                    percent: activeDiscount.discount,
                    learnMoreHref: "/about/discounts",
                });
                lsHasSeenDiscountModal.setItem(true);
            }, 3000); // 3 seconds

            return () => clearTimeout(timeout);
        }
    }, [activeDiscount]);

    const allRoute = (
        <>
            <Route element={<ProtectedRoute />}>
                {protected_route.map((route: IRoute, key) => (
                    <Route key={key} path={route.path} element={<route.element />} />
                ))}
            </Route>
            <Route element={<PublicRoute />}>
                {public_route.map((route: IRoute, key) => (
                    <Route key={key} path={route.path} element={<route.element />} />
                ))}
            </Route>
            <Route element={<AuthRoute />}>
                {auth_route.map((route: IRoute, key) => (
                    <Route key={key} path={route.path} element={<route.element />} />
                ))}
            </Route>
        </>
    );

    return (
        <>
            {/* Handle Apple OAuth callback globally */}
            <AppleOAuthHandler />
            <GoogleOAuthHandler />
            <SiteJsonLd />
            <GTMPageView />
            <Routes>
                {!isLoggedIn ? (
                    allRoute
                ) : shouldVerifyEmail || needsRealEmail ? (
                    <Route element={<SetupProfileRoute />}>
                        <Route path="*" element={<ForgotPassword />} />
                    </Route>
                ) : isProfileSetupInProgress ? (
                    <Route element={<SetupProfileRoute />}>
                        <Route path="*" element={<SetupLoaderComponent />} />
                    </Route>
                ) : (
                    allRoute
                )}
            </Routes>

            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick={true}
                rtl={false}
                draggable={true}
                theme="colored"
                transition={Slide}
            />
            <PromisedContainer />
            {/* Outside <Routes> so the header badge can raise it from any page,
                and so it survives the navigation the sign-up flow performs. */}
            <SignupPasswordModal />
        </>
    );
}
export default App;
